import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Building2,
  MapPin,
  DollarSign,
  Search,
  Filter,
  Phone,
  Mail,
  User,
  Instagram,
  Briefcase,
  Clock,
  Calendar,
  CheckCircle,
  Loader2,
  Image,
  Wifi,
  Car,
  Archive,
  Shirt,
  DoorClosed,
  Users,
  Droplet,
  Sun,
  Thermometer,
  UserCheck,
  Coffee,
  Box,
  ChevronLeft,
  ChevronRight,
  X,
  Send,
  Sparkles
} from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const AMENITY_ICONS = {
  wifi: Wifi,
  parking: Car,
  storage: Archive,
  washer_dryer: Shirt,
  private_suite: DoorClosed,
  shared_space: Users,
  sink: Droplet,
  natural_light: Sun,
  ac: Thermometer,
  reception: UserCheck,
  break_room: Coffee,
  towel_service: Shirt,
  product_storage: Box,
  "24_7_access": Clock,
};

const STANDARD_AMENITIES = [
  { id: "wifi", name: "Free WiFi" },
  { id: "parking", name: "Parking Available" },
  { id: "storage", name: "Personal Storage" },
  { id: "washer_dryer", name: "Washer/Dryer" },
  { id: "private_suite", name: "Private Suite" },
  { id: "shared_space", name: "Shared Space" },
  { id: "sink", name: "Shampoo Bowl/Sink" },
  { id: "natural_light", name: "Natural Lighting" },
  { id: "ac", name: "Climate Control" },
  { id: "reception", name: "Reception Area" },
  { id: "break_room", name: "Break Room Access" },
  { id: "towel_service", name: "Towel Service" },
  { id: "product_storage", name: "Product Storage" },
  { id: "24_7_access", name: "24/7 Access" },
];

const PublicBoothListingsPage = () => {
  const navigate = useNavigate();
  const { listingId } = useParams();  // For direct booth link /booth/:listingId
  const [searchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchCity, setSearchCity] = useState(searchParams.get("city") || "");
  const [searchState, setSearchState] = useState(searchParams.get("state") || "");
  const [selectedListing, setSelectedListing] = useState(null);
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const [applicationForm, setApplicationForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    professional_type: "",
    license_number: "",
    years_experience: 0,
    preferred_days: [],
    preferred_hours: "",
    portfolio_url: "",
    instagram_handle: "",
    about_me: "",
    why_interested: "",
    move_in_date: "",
  });

  useEffect(() => {
    if (listingId) {
      // Direct link to a specific booth - fetch and open it
      fetchSingleListing(listingId);
    } else {
      fetchListings();
    }
  }, [listingId]);

  const fetchSingleListing = async (id) => {
    try {
      const response = await axios.get(`${API}/booths/public/listing/${id}`);
      if (response.data.listing) {
        setSelectedListing(response.data.listing);
        setShowApplyDialog(true);
        // Also fetch other listings for browsing
        const allResponse = await axios.get(`${API}/booths/public/listings`);
        setListings(allResponse.data.listings || []);
      }
    } catch (error) {
      console.error("Failed to fetch listing:", error);
      toast.error("Booth listing not found");
      // Fall back to listing all booths
      fetchListings();
    } finally {
      setLoading(false);
    }
  };

  const fetchListings = async () => {
    try {
      const params = new URLSearchParams();
      if (searchCity) params.append("city", searchCity);
      if (searchState) params.append("state", searchState);

      const response = await axios.get(`${API}/booths/public/listings?${params.toString()}`);
      setListings(response.data.listings || []);
    } catch (error) {
      console.error("Failed to fetch listings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setLoading(true);
    fetchListings();
  };

  const openApplyDialog = (listing) => {
    setSelectedListing(listing);
    setCurrentImageIndex(0);
    setShowApplyDialog(true);
    setSubmitted(false);
    setApplicationForm({
      full_name: "",
      email: "",
      phone: "",
      professional_type: "",
      license_number: "",
      years_experience: 0,
      preferred_days: [],
      preferred_hours: "",
      portfolio_url: "",
      instagram_handle: "",
      about_me: "",
      why_interested: "",
      move_in_date: "",
    });
  };

  const submitApplication = async () => {
    if (!applicationForm.full_name || !applicationForm.email || !applicationForm.phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/booths/apply`, {
        booth_id: selectedListing.listing_id,
        salon_id: selectedListing.salon_id,
        ...applicationForm,
      });
      setSubmitted(true);
      toast.success("Application submitted successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit application");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleDay = (day) => {
    setApplicationForm((prev) => ({
      ...prev,
      preferred_days: prev.preferred_days.includes(day)
        ? prev.preferred_days.filter((d) => d !== day)
        : [...prev.preferred_days, day],
    }));
  };

  const getPrimaryImage = (listing) => {
    const primary = listing.images?.find((img) => img.is_primary);
    return primary?.image_url || listing.images?.[0]?.image_url || null;
  };

  const nextImage = () => {
    if (selectedListing?.images?.length > 0) {
      setCurrentImageIndex((prev) => 
        prev === selectedListing.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (selectedListing?.images?.length > 0) {
      setCurrentImageIndex((prev) => 
        prev === 0 ? selectedListing.images.length - 1 : prev - 1
      );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-amber-500 mx-auto mb-4" />
          <p className="text-stone-400">Loading booth listings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900">
      {/* Header */}
      <header className="bg-stone-900/80 border-b border-stone-700 sticky top-0 z-50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-black" />
              </div>
              <div>
                <h1 className="font-bold text-white text-lg">Booth Rentals</h1>
                <p className="text-stone-400 text-xs">Find your perfect salon space</p>
              </div>
            </div>
            <Button
              variant="outline"
              className="border-stone-600 text-stone-300 hover:bg-stone-700"
              onClick={() => navigate("/")}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border-b border-stone-700">
        <div className="max-w-7xl mx-auto px-4 py-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Find Your <span className="text-amber-400">Perfect Booth</span>
          </h2>
          <p className="text-stone-400 max-w-2xl mx-auto mb-8">
            Browse available booth rentals from top salons. Find the space that fits your style and start your journey to independence.
          </p>

          {/* Search */}
          <div className="flex flex-col sm:flex-row gap-3 max-w-2xl mx-auto">
            <div className="flex-1 relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
              <Input
                value={searchCity}
                onChange={(e) => setSearchCity(e.target.value)}
                placeholder="City (e.g., Houston)"
                className="pl-10 bg-stone-800 border-stone-600 text-white"
              />
            </div>
            <Input
              value={searchState}
              onChange={(e) => setSearchState(e.target.value)}
              placeholder="State (e.g., TX)"
              className="w-24 bg-stone-800 border-stone-600 text-white"
            />
            <Button onClick={handleSearch} className="bg-amber-500 hover:bg-amber-600 text-black">
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
          </div>
        </div>
      </div>

      {/* Listings Grid */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <p className="text-stone-400">
            {listings.length} booth{listings.length !== 1 ? "s" : ""} available
          </p>
        </div>

        {listings.length === 0 ? (
          <Card className="bg-stone-800/50 border-stone-700">
            <CardContent className="py-16 text-center">
              <Building2 className="w-16 h-16 mx-auto text-stone-600 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Booths Available</h3>
              <p className="text-stone-400 mb-6">
                {searchCity || searchState
                  ? "No booths found in this area. Try a different location."
                  : "Check back soon for new listings!"}
              </p>
              {(searchCity || searchState) && (
                <Button
                  variant="outline"
                  className="border-stone-600"
                  onClick={() => {
                    setSearchCity("");
                    setSearchState("");
                    fetchListings();
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {listings.map((listing) => (
              <Card
                key={listing.listing_id}
                className="bg-stone-800/50 border-stone-700 overflow-hidden hover:border-amber-500/50 transition-all cursor-pointer group"
                onClick={() => openApplyDialog(listing)}
              >
                {/* Image */}
                <div className="relative h-48 bg-stone-900">
                  {getPrimaryImage(listing) ? (
                    <img
                      src={getPrimaryImage(listing)}
                      alt={listing.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Image className="w-16 h-16 text-stone-700" />
                    </div>
                  )}
                  <Badge className="absolute top-3 left-3 bg-green-500">Available</Badge>
                  {listing.images?.length > 1 && (
                    <Badge className="absolute top-3 right-3 bg-stone-800/80">
                      {listing.images.length} photos
                    </Badge>
                  )}
                </div>

                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="text-white font-semibold text-lg">{listing.name}</h3>
                      {listing.salon && (
                        <p className="text-stone-400 text-sm">
                          {listing.salon.name}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-amber-400 font-bold">${listing.weekly_rate}/wk</p>
                      {listing.monthly_rate && (
                        <p className="text-stone-500 text-xs">${listing.monthly_rate}/mo</p>
                      )}
                    </div>
                  </div>

                  {/* Location */}
                  {listing.salon && (listing.salon.city || listing.salon.state) && (
                    <div className="flex items-center gap-1 text-stone-400 text-sm mb-3">
                      <MapPin className="w-3 h-3" />
                      {[listing.salon.city, listing.salon.state].filter(Boolean).join(", ")}
                    </div>
                  )}

                  {/* Description */}
                  {listing.description && (
                    <p className="text-stone-400 text-sm mb-3 line-clamp-2">{listing.description}</p>
                  )}

                  {/* Amenities */}
                  {listing.amenities?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {listing.amenities.slice(0, 3).map((amenityId) => {
                        const amenity = STANDARD_AMENITIES.find((a) => a.id === amenityId);
                        const Icon = AMENITY_ICONS[amenityId] || CheckCircle;
                        return amenity ? (
                          <Badge key={amenityId} variant="outline" className="text-xs border-stone-600 text-stone-400">
                            <Icon className="w-3 h-3 mr-1" />
                            {amenity.name}
                          </Badge>
                        ) : null;
                      })}
                      {listing.amenities.length > 3 && (
                        <Badge variant="outline" className="text-xs border-stone-600 text-stone-400">
                          +{listing.amenities.length - 3} more
                        </Badge>
                      )}
                    </div>
                  )}

                  <Button className="w-full bg-amber-500 hover:bg-amber-600 text-black mt-2">
                    View Details & Apply
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Application Dialog */}
      <Dialog open={showApplyDialog} onOpenChange={setShowApplyDialog}>
        <DialogContent className="bg-stone-800 border-stone-700 max-w-4xl max-h-[90vh] overflow-y-auto">
          {submitted ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Application Submitted!</h3>
              <p className="text-stone-400 mb-6">
                The salon owner will review your application and get back to you soon.
              </p>
              <Button
                onClick={() => setShowApplyDialog(false)}
                className="bg-amber-500 hover:bg-amber-600 text-black"
              >
                Close
              </Button>
            </div>
          ) : selectedListing ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-white text-xl">Apply for {selectedListing.name}</DialogTitle>
              </DialogHeader>

              <div className="grid md:grid-cols-2 gap-6 mt-4">
                {/* Left: Booth Details */}
                <div className="space-y-4">
                  {/* Image Gallery */}
                  {selectedListing.images?.length > 0 && (
                    <div className="relative rounded-lg overflow-hidden">
                      <img
                        src={selectedListing.images[currentImageIndex]?.image_url}
                        alt={`${selectedListing.name} ${currentImageIndex + 1}`}
                        className="w-full h-64 object-cover"
                      />
                      {selectedListing.images.length > 1 && (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); prevImage(); }}
                            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70"
                          >
                            <ChevronLeft className="w-5 h-5" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); nextImage(); }}
                            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70"
                          >
                            <ChevronRight className="w-5 h-5" />
                          </button>
                          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                            {selectedListing.images.map((_, idx) => (
                              <div
                                key={idx}
                                className={`w-2 h-2 rounded-full ${idx === currentImageIndex ? "bg-amber-500" : "bg-white/50"}`}
                              />
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Booth Info */}
                  <div className="bg-stone-700/30 rounded-lg p-4 space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="text-white font-semibold text-lg">{selectedListing.name}</h4>
                        {selectedListing.salon?.name && (
                          <p className="text-stone-400 text-sm">{selectedListing.salon.name}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-amber-400 font-bold text-xl">${selectedListing.weekly_rate}/wk</p>
                        {selectedListing.monthly_rate && (
                          <p className="text-stone-400 text-sm">${selectedListing.monthly_rate}/mo</p>
                        )}
                      </div>
                    </div>

                    {selectedListing.description && (
                      <p className="text-stone-300 text-sm">{selectedListing.description}</p>
                    )}

                    {selectedListing.deposit_amount && (
                      <p className="text-stone-400 text-sm">
                        <span className="text-stone-500">Deposit:</span> ${selectedListing.deposit_amount}
                      </p>
                    )}

                    {selectedListing.salon && (selectedListing.salon.city || selectedListing.salon.state) && (
                      <div className="flex items-center gap-2 text-stone-300 text-sm">
                        <MapPin className="w-4 h-4 text-amber-500" />
                        {[selectedListing.salon.address, selectedListing.salon.city, selectedListing.salon.state]
                          .filter(Boolean)
                          .join(", ")}
                      </div>
                    )}

                    {selectedListing.salon?.phone && (
                      <div className="flex items-center gap-2 text-stone-300 text-sm">
                        <Phone className="w-4 h-4 text-amber-500" />
                        {selectedListing.salon.phone}
                      </div>
                    )}
                  </div>

                  {/* Amenities */}
                  {selectedListing.amenities?.length > 0 && (
                    <div className="bg-stone-700/30 rounded-lg p-4">
                      <h5 className="text-white font-medium mb-3">Amenities</h5>
                      <div className="grid grid-cols-2 gap-2">
                        {selectedListing.amenities.map((amenityId) => {
                          const amenity = STANDARD_AMENITIES.find((a) => a.id === amenityId);
                          const Icon = AMENITY_ICONS[amenityId] || CheckCircle;
                          return amenity ? (
                            <div key={amenityId} className="flex items-center gap-2 text-stone-300 text-sm">
                              <Icon className="w-4 h-4 text-amber-500" />
                              {amenity.name}
                            </div>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Right: Application Form */}
                <div className="space-y-4">
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                    <p className="text-amber-400 text-sm flex items-center gap-2">
                      <Sparkles className="w-4 h-4" />
                      Fill out this form to apply for this booth rental
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <Label className="text-stone-300">Full Name *</Label>
                      <Input
                        value={applicationForm.full_name}
                        onChange={(e) => setApplicationForm({ ...applicationForm, full_name: e.target.value })}
                        placeholder="Your full name"
                        className="bg-stone-700 border-stone-600 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-stone-300">Email *</Label>
                      <Input
                        type="email"
                        value={applicationForm.email}
                        onChange={(e) => setApplicationForm({ ...applicationForm, email: e.target.value })}
                        placeholder="your@email.com"
                        className="bg-stone-700 border-stone-600 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-stone-300">Phone *</Label>
                      <Input
                        value={applicationForm.phone}
                        onChange={(e) => setApplicationForm({ ...applicationForm, phone: e.target.value })}
                        placeholder="(555) 123-4567"
                        className="bg-stone-700 border-stone-600 text-white"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-stone-300">Professional Type</Label>
                      <Select
                        value={applicationForm.professional_type}
                        onValueChange={(v) => setApplicationForm({ ...applicationForm, professional_type: v })}
                      >
                        <SelectTrigger className="bg-stone-700 border-stone-600 text-white">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="barber">Barber</SelectItem>
                          <SelectItem value="hairstylist">Hairstylist</SelectItem>
                          <SelectItem value="nail_tech">Nail Technician</SelectItem>
                          <SelectItem value="lash_tech">Lash Technician</SelectItem>
                          <SelectItem value="massage_therapist">Massage Therapist</SelectItem>
                          <SelectItem value="esthetician">Esthetician</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-stone-300">Years Experience</Label>
                      <Input
                        type="number"
                        value={applicationForm.years_experience || ""}
                        onChange={(e) =>
                          setApplicationForm({ ...applicationForm, years_experience: parseInt(e.target.value) || 0 })
                        }
                        placeholder="0"
                        className="bg-stone-700 border-stone-600 text-white"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-stone-300">License Number</Label>
                    <Input
                      value={applicationForm.license_number}
                      onChange={(e) => setApplicationForm({ ...applicationForm, license_number: e.target.value })}
                      placeholder="State license number"
                      className="bg-stone-700 border-stone-600 text-white"
                    />
                  </div>

                  <div>
                    <Label className="text-stone-300">Preferred Days</Label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                        <Button
                          key={day}
                          type="button"
                          size="sm"
                          variant={applicationForm.preferred_days.includes(day) ? "default" : "outline"}
                          className={
                            applicationForm.preferred_days.includes(day)
                              ? "bg-amber-500 text-black"
                              : "border-stone-600 text-stone-300"
                          }
                          onClick={() => toggleDay(day)}
                        >
                          {day}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-stone-300">Preferred Hours</Label>
                    <Input
                      value={applicationForm.preferred_hours}
                      onChange={(e) => setApplicationForm({ ...applicationForm, preferred_hours: e.target.value })}
                      placeholder="e.g., 9am - 6pm"
                      className="bg-stone-700 border-stone-600 text-white"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-stone-300">Portfolio URL</Label>
                      <Input
                        value={applicationForm.portfolio_url}
                        onChange={(e) => setApplicationForm({ ...applicationForm, portfolio_url: e.target.value })}
                        placeholder="https://..."
                        className="bg-stone-700 border-stone-600 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-stone-300">Instagram</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500">@</span>
                        <Input
                          value={applicationForm.instagram_handle}
                          onChange={(e) =>
                            setApplicationForm({ ...applicationForm, instagram_handle: e.target.value.replace("@", "") })
                          }
                          placeholder="handle"
                          className="bg-stone-700 border-stone-600 text-white pl-8"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="text-stone-300">Desired Move-in Date</Label>
                    <Input
                      type="date"
                      value={applicationForm.move_in_date}
                      onChange={(e) => setApplicationForm({ ...applicationForm, move_in_date: e.target.value })}
                      className="bg-stone-700 border-stone-600 text-white"
                    />
                  </div>

                  <div>
                    <Label className="text-stone-300">About You</Label>
                    <Textarea
                      value={applicationForm.about_me}
                      onChange={(e) => setApplicationForm({ ...applicationForm, about_me: e.target.value })}
                      placeholder="Tell us about your experience and specialties..."
                      className="bg-stone-700 border-stone-600 text-white"
                      rows={2}
                    />
                  </div>

                  <div>
                    <Label className="text-stone-300">Why are you interested in this booth?</Label>
                    <Textarea
                      value={applicationForm.why_interested}
                      onChange={(e) => setApplicationForm({ ...applicationForm, why_interested: e.target.value })}
                      placeholder="What attracted you to this location?"
                      className="bg-stone-700 border-stone-600 text-white"
                      rows={2}
                    />
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button
                      variant="outline"
                      className="flex-1 border-stone-600"
                      onClick={() => setShowApplyDialog(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="flex-1 bg-amber-500 hover:bg-amber-600 text-black"
                      onClick={submitApplication}
                      disabled={submitting}
                    >
                      {submitting ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Submit Application
                    </Button>
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublicBoothListingsPage;
